import { defineStore } from "pinia"
import { BaseUser, CompanyMember, CompanyMemberTypeEnum, Member, UserTypeEnum } from "@vitable/asclepius-api-client-ts"
import router from "@/router"
import eventBus from "@common/events/common-event-bus"
import events from "@/modules/login/events/events"
import { useListCompanyMembersByMemberId } from "@/composables/company-members"
import { useGetMe } from "@/composables/auth"
import { useEnrollmentStore } from "@/modules/enrollment-new/stores/enrollment-new-store"
import { useI18nStore } from "@/store/i18n-store"
import { useSubscriptionStore } from "@/store/subscription-store"
import { usePhoneClaimGateStore } from "@/modules/phone-claim/stores/phone-claim-gate-store"
import asclepiusConfig from "@common/config/asclepius-config"

export interface UserState {
  user: Nullable<BaseUser>
  companyMembers: Array<CompanyMember>
  token: Nullable<string>
  refreshToken: Nullable<string>
}

async function _onLogInSuccess() {
  const userStore = useUserStore()
  const enrollmentStore = useEnrollmentStore()
  const i18nStore = useI18nStore()

  if (userStore.user!.user_type === UserTypeEnum.Member) {
    await Promise.all([userStore.loadLinkedCompanyMembers(), enrollmentStore.loadAllPendingCompanyBenefitElections()])
    if (i18nStore.getLanguage === "en") i18nStore.setLanguage(userStore.user!.member!.preferred_language!)
  }

  eventBus.$emit(events.LOG_IN)
}

export const useUserStore = defineStore("userStore", {
  persist: true,
  state: (): UserState => {
    return {
      token: null,
      refreshToken: null,
      companyMembers: [],
      user: null
    }
  },
  getters: {
    isLoggedIn(): boolean {
      return !!this.token && !!this.user && !!this.refreshToken
    },
    member(): Member | undefined {
      return this.user?.member
    },
    memberId(): string | undefined {
      return this.user?.member?.id
    },
    /** Prefixed `mbr_…` id that public member API routes take. */
    publicMemberId(): string | undefined {
      return this.user?.member?.member_id
    },
    isCompanyMember(): boolean {
      return !!this.companyMembers.length
    },
    companyEmployees(): Array<CompanyMember> {
      return this.companyMembers.filter((cm) => cm.type === CompanyMemberTypeEnum.Employee)
    },
    activeCompanyEmployees(): Array<CompanyMember> {
      return this.companyMembers.filter((cm) => cm.type === CompanyMemberTypeEnum.Employee && cm.active_in)
    },
    companyRecruits(): Array<CompanyMember> {
      return this.companyMembers.filter((cm) => cm.type === CompanyMemberTypeEnum.Recruit)
    }
  },
  actions: {
    setUser(user: Nullable<BaseUser>) {
      this.user = user
    },

    setAuthTokens(accessToken: string, refreshToken: string) {
      this.token = accessToken
      this.refreshToken = refreshToken
      asclepiusConfig.setAccessToken(accessToken)
      asclepiusConfig.setRefreshToken(refreshToken)
    },

    async loadLinkedCompanyMembers() {
      if (!this.user || this.user.user_type !== UserTypeEnum.Member)
        throw new Error("Cannot load linked company members w null user or non Member type user!")

      const { companyMembers, listCompanyMembersByMemberId } = useListCompanyMembersByMemberId()
      await listCompanyMembersByMemberId({ memberId: this.user.member!.id })
      this.companyMembers = companyMembers.value
    },

    async init() {
      const userStore: string | null = window.localStorage.getItem("userStore")

      if (userStore) {
        const store: UserState = JSON.parse(userStore)
        if (store.user && store.token && store.refreshToken) {
          this.setAuthTokens(store.token, store.refreshToken)
          try {
            await this.reloadMe()
          } catch {
            this.logout()
            return
          }
          if (!this.user) {
            this.logout()
            return
          }
          // Phone claim gate re-derives from the member on every cold start
          usePhoneClaimGateStore().evaluateForUser(this.user!)
          await _onLogInSuccess()
        }
      }
    },

    async reloadMe() {
      const { getMe, me } = useGetMe()
      await getMe()
      this.setUser(me.value!)
    },

    async login(user: BaseUser, accessToken: string, refreshToken: string) {
      this.setUser(user)
      this.setAuthTokens(accessToken, refreshToken)
      await _onLogInSuccess()
    },

    logout() {
      this.$reset()
      asclepiusConfig.clearAuthTokens()

      useEnrollmentStore().$reset()
      useI18nStore().$reset()
      useSubscriptionStore().$reset()

      if (router.currentRoute.value.path !== "/") {
        router.push({ name: "login" }).then()
      }
      eventBus.$emit(events.LOG_OUT)
    }
  }
})
